import React, {useEffect, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import FlexBox from "view/components/Layout/FlexBox";
import {Collapse, Typography} from "@material-ui/core";
import {CardOrder} from "ts/api/order";
import {getOrderSummaryRows} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/OrderSummary/utils/getOrderSummaryRows";
import {CheckCircleOutline, UnfoldLess, UnfoldMore} from "@material-ui/icons";
import IconButton from "@material-ui/core/IconButton";
import OrderSummaryRow from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/OrderSummary/OrderSummaryRow";
import clsx from "clsx";
import {green, red} from "@material-ui/core/colors";
import CancelIcon from "@material-ui/icons/Cancel";

import {IShow} from "@evennt-tech/api-v2-core";
import useStyles from "./styles";

type OrderSummaryProps = {
  order: CardOrder;
  hasError: boolean;
  isPaid: boolean;
  show: IShow;
};

const OrderSummary = ({order, hasError, isPaid, show}: OrderSummaryProps) => {
  const classes = useStyles();
  const {t} = useTranslate();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const handleToggle = () => setIsCollapsed((prev) => !prev);

  const allSummaryRows = useMemo(() => {
    if (!show || !order) {
      return [];
    }

    return getOrderSummaryRows(show, order, t);
  }, [show, order, t]);

  useEffect(() => {
    if (order) {
      setIsCollapsed(false);
    }
  }, [order]);
  if (!show) {
    return null;
  }

  const totalCostRow = allSummaryRows[allSummaryRows.length - 1];
  const orderSummaryRows = allSummaryRows.slice(0, allSummaryRows.length - 1);

  const Icon = isCollapsed ? UnfoldMore : UnfoldLess;
  const actionPanel = (() => {
    if (isPaid) {
      return <PaymentStatus />;
    }
    if (hasError) {
      return <PaymentStatus isFailed />;
    }

    return null;
  })();

  return (
    <FlexBox
      className={clsx(classes.root, {[classes.error]: hasError})}
      column
      fullWidth
    >
      <Collapse in={isCollapsed} className={classes.collapse}>
        {isCollapsed &&
          orderSummaryRows.map((summary, idx, arr) => (
            <OrderSummaryRow
              key={idx}
              rowData={summary}
              isLast={idx === arr.length}
            />
          ))}
      </Collapse>
      <OrderSummaryRow
        rowData={totalCostRow}
        actionPanel={
          <FlexBox gap={3} alignCenter>
            {actionPanel}
            <IconButton onClick={handleToggle}>
              <Icon />
            </IconButton>
          </FlexBox>
        }
        isHighlight
        isLast
      />
    </FlexBox>
  );
};

const PaymentStatus = ({isFailed}: {isFailed?: boolean}) => {
  const classes = useStyles();
  const border = `1px solid ${isFailed ? red[500] : green[500]}`;

  return (
    <FlexBox
      alignCenter
      gap={1.25}
      className={classes.paymentStatus}
      border={border}
    >
      {!isFailed && (
        <CheckCircleOutline
          htmlColor={green[500]}
          style={{backgroundColor: green[50], borderRadius: "150%"}}
        />
      )}
      {!!isFailed && <CancelIcon htmlColor={red[500]} />}
      <Typography
        variant="h4"
        style={{fontWeight: 500, color: isFailed ? red[500] : green[500]}}
      >
        {isFailed ? (
          <T keyName="exhibitor.cart.payment.status.failed" />
        ) : (
          <T keyName="exhibitor.cart.payment.status.paid" />
        )}
      </Typography>
    </FlexBox>
  );
};

export default OrderSummary;
